import { t } from '@transifex/native';
import { useState } from 'react';
import type { JSX, ChangeEvent } from 'react';

import * as styles from 'pkg/config/styles';

import * as api from 'pkg/api';
import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { fullName } from 'pkg/api/models/account/fmt';

import { useSmallScreen } from 'components/MediaQuery';
import SectionDescription from 'components/SectionDescription';
import SectionTitle from 'components/section_title';
import { useStepModalContext } from 'components/step-modal';

import * as Input from 'components/form/inputs';
import Column from 'components/layout/column';
import Row from 'components/layout/row';

import Button from 'design/button';
import Divider from 'design/divider';

// Substrings matched here are defined in
// api/pkg/actions/account_invite_actions/create.go and wrapped by the API as
// {"error": "invalid input: <message>"}. Keep in sync if those strings change.
function sendInviteErrorTitle(error?: string): string {
	if (error?.includes('cannot send invite to yourself')) {
		return t("You can't invite yourself.");
	}

	if (error?.includes('already parent to the child')) {
		return t('This person is already a parent for this child.');
	}

	return t('Could not send invite. Please try again.');
}

interface EmailStepProps {
	targetAccounts?: models.account.Account[];
	email?: string;
	setEmail: (email: string) => void;
	setInviteKey: (link: string) => void;
	multipleChildren: boolean;
}

export default function EmailInviteStep({
	targetAccounts,
	email,
	setEmail,
	setInviteKey,
	multipleChildren,
}: EmailStepProps): JSX.Element {
	const { goTo } = useStepModalContext();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const targets = targetAccounts.map((targetAccount) => {
		return { type: 'child', targetId: targetAccount.id };
	});

	const handleSetEmail = (event: ChangeEvent<HTMLInputElement>) => {
		setEmail(event.target.value);
		setErrorMessage(undefined);
	};

	const handleSendInvite = async () => {
		setErrorMessage(undefined);

		const [request, response] = await api.post<models.APIError>(
			endpoints.AccountInvites.Index(),
			{
				email,
				targets,
			}
		);

		if (!request.ok) {
			setErrorMessage(sendInviteErrorTitle(response?.error));

			return;
		}

		goTo('email-invite-sent');
	};

	const generateInviteLink = async () => {
		setErrorMessage(undefined);

		const [request, response] =
			await api.post<models.accountInvite.AccountInvite>(
				endpoints.AccountInvites.Index(),
				{
					targets,
				}
			);

		if (!request.ok) {
			setErrorMessage(t('Could not generate invite link. Please try again.'));

			return;
		}

		setInviteKey(response.key);
		goTo('invite-link-generated');
	};

	const isSmallScreen = useSmallScreen();
	const validEmail = !!email && email.includes('@');

	let title: string;
	let description: string;

	if (multipleChildren) {
		title = t('Connect a parent to your children!');

		description = t(
			'Add the email address of the parent that you want to connect to your children! If they already have a 360Player account, make sure to use that email.'
		);
	} else {
		const targetAccount = targetAccounts[0];

		title = t('Connect a parent to {child_name}!', {
			child_name: fullName(targetAccount),
		});

		description = t(
			'Add the email address of the parent that you want to connect to {child_first_name}! If they already have a 360Player account, make sure to use that email.',
			{ child_first_name: targetAccount.firstName }
		);
	}

	return (
		<Column spacing={styles.spacing._8}>
			<Column spacing={styles.spacing._2}>
				<SectionTitle>{title}</SectionTitle>
				<SectionDescription>{description}</SectionDescription>
			</Column>

			<Column>
				<Input.Group label={t('Email')} errorMessage={errorMessage}>
					<Row collapseOnSmallScreens align="center" columns="1fr auto">
						<Input.Field onChange={handleSetEmail} value={email} type="email" />
						<Button
							primary
							block={isSmallScreen}
							label={t('Send invite')}
							disabled={!validEmail}
							onClick={handleSendInvite}
						/>
					</Row>
				</Input.Group>
			</Column>

			<Divider text={t('or')} small />

			<Button
				secondary
				label={t('Generate invite link')}
				icon="ios_share"
				onClick={generateInviteLink}
			/>
		</Column>
	);
}
